/**
 * ClientApiFeatureFlagsProvider — browser-side implementation of the
 * `IFeatureFlags` interface from `@order-up/analytics`, backed by the
 * Fastify per-caller evaluation endpoint
 * `GET /api/v1/feature-flags/evaluate/:key`.
 *
 * Why this exists
 * ---------------
 * The hot-path provider in `@order-up/core`
 * (`SupabaseFeatureFlagsProvider`) talks to Postgres directly with the
 * service-role key and opens a Realtime channel — both of which are
 * server-only. In Next.js apps the `<AnalyticsProvider>` lives in a
 * client boundary, so `useFeatureFlag()` consumers need a client-safe
 * implementation. This provider is that.
 *
 * Tenant context
 * --------------
 * Intentionally tenant-agnostic on the client. The Fastify
 * `/api/v1/feature-flags/evaluate/:key` route resolves the tenant from
 * the caller's Firebase ID token (`evaluateFlag` runs through
 * `createApiClient`, which already attaches the bearer token). Putting
 * tenant resolution in the API — not in the client provider — keeps the
 * single source of truth on the server, matches the way the rest of the
 * client services (carts, checkouts, books, …) work, and means a
 * tenant-switch on the auth side does not require any provider
 * reconfiguration here. Calling `reload()` after a tenant switch
 * invalidates the local cache so subsequent reads re-fetch fresh.
 *
 * Caching
 * -------
 * In-memory `Map<flagKey, { value, expiresAt }>`, 30 s TTL. `IFeatureFlags`
 * is a synchronous interface, so we cannot block the React render path on
 * a network request. On a cache miss we synchronously return the caller's
 * default value and fire a background fetch; once the fetch resolves we
 * cache the result and notify subscribers, which lets a wrapping
 * `useSyncExternalStore` (or a parent hook re-render via React Query's
 * existing flow) pick up the freshly resolved value on the next tick.
 *
 * `getAllFlags()`
 * ---------------
 * Returns whatever the local cache has accumulated so far, projected to
 * the analytics-package shape (`boolean | string`). This is a deliberate
 * trade-off: there is no per-tenant bulk-evaluate endpoint, and the
 * admin `listFeatureFlags` returns the catalog with global default
 * values — the wrong shape for a tenant-aware consumer. The interface
 * documents this method as a debug surface, so partial population is
 * acceptable.
 *
 * `onFlagChange()`
 * ----------------
 * Maintains a subscriber list. Subscribers are notified whenever the
 * background fetch fired by `isEnabled()` / `getVariant()` resolves with
 * a value different from the current cache (or fills a previously empty
 * slot). Server-side change events (admin flips, schedule fires) reach
 * this provider only through the next refetch — there is no client
 * Realtime channel by design. The 30 s TTL bounds staleness; consumers
 * that need eager-fresh values should call `reload()` at the right time
 * (e.g. on a route transition).
 */

import type { IFeatureFlags } from '@order-up/analytics';
import { evaluateFlag } from '../services/featureFlags';
import { createLogger } from '../utils/logger';

const logger = createLogger('ClientApiFeatureFlagsProvider');

const CACHE_TTL_MS = 30_000;

/**
 * Fail-open error TTL schedule. Mirrors `firebaseUidLinker.ts`'s
 * `TRANSIENT_BASE_SECONDS` / `TRANSIENT_CAP_SECONDS` doubling backoff:
 *   1 → 5s, 2 → 10s, 3 → 20s, 4 → 40s, 5+ → 60s
 *
 * Without this, a failed `evaluateFlag` returns `undefined` from the
 * catch and the cache is never written. Every subsequent React render
 * calls `isEnabled()` / `getVariant()`, sees an empty cache slot, and
 * fires a fresh `refresh()` — collapsing the polling cadence from the
 * intended 30 s TTL to whatever re-render trigger is fastest (typically
 * 6–10 s for a dashboard with parent React-Query refetches). When the
 * API endpoint is healthy this is invisible; when it's down (Supabase
 * outage, expired session, etc.) it spam-polls the dead endpoint.
 *
 * Caching a `null` value entry preserves caller-supplied default
 * behavior — `readCache()` returns `null`, `isEnabled()` coerces null
 * to the caller's `defaultValue`, and `getVariant()` returns the
 * caller's `defaultValue` directly.
 */
const ERROR_BASE_TTL_MS = 5_000;
const ERROR_CAP_TTL_MS = 60_000;

function errorTtlMs(errorCount: number): number {
  return Math.min(
    ERROR_BASE_TTL_MS * Math.pow(2, Math.max(0, errorCount - 1)),
    ERROR_CAP_TTL_MS,
  );
}

interface CacheEntry {
  value: unknown;
  expiresAt: number;
}

type FlagChangeCallback = (
  flags: Record<string, boolean | string>,
) => void;

/**
 * Project an arbitrary flag value to the `boolean | string` shape that
 * the analytics interface's `getAllFlags()` debug surface uses.
 * Numbers and JSON values are dropped (they're still readable via
 * `getVariant<T>()` / the per-flag cache).
 */
function projectForDebug(value: unknown): boolean | string | null {
  if (typeof value === 'boolean' || typeof value === 'string') return value;
  return null;
}

/**
 * Coerce an arbitrary flag value to a boolean for `isEnabled()`. Numeric
 * and JSON-shaped flags fall back to JavaScript truthiness, which is what
 * a caller asking "is this on?" is expressing intent for. The default
 * value still wins on cache misses.
 */
function coerceBoolean(value: unknown, defaultValue: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (value === null || value === undefined) return defaultValue;
  return Boolean(value);
}

export class ClientApiFeatureFlagsProvider implements IFeatureFlags {
  readonly providerName = 'ClientApiFeatureFlags';

  private readonly cache: Map<string, CacheEntry> = new Map();
  /** flagKeys with an in-flight fetch (de-duplicates concurrent reads). */
  private readonly inFlight: Map<string, Promise<unknown>> = new Map();
  /** Consecutive error count per flagKey — drives `errorTtlMs()` backoff. */
  private readonly errorCounts: Map<string, number> = new Map();
  private readonly subscribers: Set<FlagChangeCallback> = new Set();

  isConfigured(): boolean {
    // The HTTP endpoint is the configuration. Even before the user is
    // authenticated, calls fail fast and we fall back to defaults — there
    // is nothing to "configure" on this provider in the SDK sense.
    return true;
  }

  isEnabled(flag: string, defaultValue = false): boolean {
    const cached = this.readCache(flag);
    if (cached !== undefined) {
      return coerceBoolean(cached, defaultValue);
    }
    void this.refresh(flag);
    return defaultValue;
  }

  getVariant<T = string>(flag: string, defaultValue?: T): T {
    const cached = this.readCache(flag);
    if (cached !== undefined) {
      return cached === null ? (defaultValue as T) : (cached as T);
    }
    void this.refresh(flag);
    return defaultValue as T;
  }

  getAllFlags(): Record<string, boolean | string> {
    const out: Record<string, boolean | string> = {};
    const now = Date.now();
    for (const [flagKey, entry] of this.cache.entries()) {
      if (entry.expiresAt <= now) continue;
      const projected = projectForDebug(entry.value);
      if (projected !== null) out[flagKey] = projected;
    }
    return out;
  }

  async reload(): Promise<void> {
    this.cache.clear();
    this.errorCounts.clear();
    // Don't pre-fetch every flag — the next render's `isEnabled()` /
    // `getVariant()` calls will lazily re-populate the cache. This
    // matches the way the server-side provider warms on demand.
    this.emitChange();
  }

  onFlagChange(callback: FlagChangeCallback): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  // ─── Internals ─────────────────────────────────────────────────────

  private readCache(flagKey: string): unknown {
    const entry = this.cache.get(flagKey);
    if (!entry) return undefined;
    if (entry.expiresAt <= Date.now()) {
      this.cache.delete(flagKey);
      return undefined;
    }
    return entry.value;
  }

  private async refresh(flagKey: string): Promise<unknown> {
    const existing = this.inFlight.get(flagKey);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const result = await evaluateFlag(flagKey);
        const previous = this.cache.get(flagKey)?.value;
        this.cache.set(flagKey, {
          value: result.value,
          expiresAt: Date.now() + CACHE_TTL_MS,
        });
        this.errorCounts.delete(flagKey);
        if (!Object.is(previous, result.value)) {
          this.emitChange();
        }
        return result.value;
      } catch (err) {
        // Fail-open with exponential backoff (5s → 60s, mirrors
        // `firebaseUidLinker`). Caching `null` makes subsequent
        // `isEnabled()` / `getVariant()` calls fall back to the
        // caller's `defaultValue` for the backoff window, instead of
        // hot-looping a fresh `evaluateFlag` fetch on every render.
        const nextCount = (this.errorCounts.get(flagKey) ?? 0) + 1;
        const ttlMs = errorTtlMs(nextCount);
        this.errorCounts.set(flagKey, nextCount);
        this.cache.set(flagKey, {
          value: null,
          expiresAt: Date.now() + ttlMs,
        });
        logger.warn('Flag evaluation failed; caching default with backoff', {
          flagKey,
          errorCount: nextCount,
          ttlMs,
          error: err instanceof Error ? err.message : String(err),
        });
        return undefined;
      } finally {
        this.inFlight.delete(flagKey);
      }
    })();

    this.inFlight.set(flagKey, promise);
    return promise;
  }

  private emitChange(): void {
    if (this.subscribers.size === 0) return;
    const snapshot = this.getAllFlags();
    for (const sub of this.subscribers) {
      try {
        sub(snapshot);
      } catch (err) {
        logger.warn('Feature-flag change subscriber threw', {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }
}

/**
 * Module-level singleton — every consumer that imports
 * `clientApiFeatureFlagsProvider` shares one cache, which is what we
 * want for a tab-wide flag store.
 */
export const clientApiFeatureFlagsProvider: IFeatureFlags =
  new ClientApiFeatureFlagsProvider();
